<script lang="ts">import { stateUi } from "@infinitro/config";
import { loggedIn, toSiteUrl } from "@infinitro/global";
import TabButton from "./parts/tab-button.svelte";
let { tabs = [], activeTab = $bindable(), width = null, size = "large", notSameSize = false, preserveScrollPosition = false, saveScrollPosition = null, borderRadius, borderRadiusContainer, shouldScrollIntoView = true, customClass = "", useVariation = false, altColumns = 2, vertical = false, align = "center", transparentBackground = false, autoFitText = true, lightMode = false, onChange } = $props();
$effect.pre(() => {
	if (activeTab === undefined) {
		activeTab = tabs.length > 0 ? tabs[0].value : "";
	}
});
let tabsContainer = $state(undefined);
let tabElements = $state([]);
const shouldShowTabs = () => (tabs?.length ?? 0) > 1;
const resolvedActiveTab = $derived(activeTab ?? tabs[0]?.value ?? "");
function handleTabClick(_event, value) {
	if (preserveScrollPosition && saveScrollPosition) {
		saveScrollPosition(window.scrollY);
	}
	activeTab = value;
	const index = tabs.findIndex((tab) => tab.value === value);
	const tab = tabs[index];
	onChange?.({
		index,
		value,
		tab
	});
}
$effect(() => {
	if (!shouldShowTabs()) return;
	const index = tabs.findIndex((tab) => tab.value === resolvedActiveTab);
	const activeTabElement = tabElements[index];
	if (shouldScrollIntoView && activeTabElement instanceof HTMLElement && tabsContainer) {
		activeTabElement.scrollIntoView({
			behavior: "smooth",
			inline: "center",
			block: "nearest"
		});
	}
});
const useAlternative = $derived($stateUi.tabs.tabsStyle === "alternative");
const useAlternative2 = $derived($stateUi.tabs.tabsStyle === "alternative2");
const useAlternative4 = $derived($stateUi.tabs.tabsStyle === "alternative4");
const useFlatNeutralSurface = $derived($stateUi.tabs?.useFlatNeutralSurface ?? false);
const compactTabRadii = $derived($stateUi.tabs?.useCompactTabStripRadius ?? false);
const resolvedBorderRadius = $derived(borderRadius ?? (compactTabRadii ? "8px" : "14px"));
const resolvedBorderRadiusContainer = $derived(borderRadiusContainer ?? (compactTabRadii ? "12px" : "16px"));
</script>

{#if shouldShowTabs()}
	<div
		class="tabs {customClass}"
		class:tabs-alternative={useAlternative || useAlternative4}
		class:tabs-alternative-2={useAlternative2}
		class:tabs-alternative-4={useAlternative4}
		class:tabs-transparent={transparentBackground}
		class:tabs-light={lightMode}
		class:tabs-flat-neutral-surface={useFlatNeutralSurface}
		class:variation={useVariation}
		class:vertical-tabs={vertical && !useVariation}
		class:vertical-tabs-alternative2={vertical && useAlternative2}
		style="
		width: {width ? width : 'auto'};
		border-radius: {resolvedBorderRadiusContainer};
		{useVariation ? `--alt-cols: ${Math.max(1, altColumns)};` : ''}
	"
		bind:this={tabsContainer}
	>
		{#each tabs as tab, index (tab.value)}
			{#if tab.link && tab.url}
				<!-- eslint-disable svelte/no-navigation-without-resolve -->
				<a
					href={toSiteUrl(tab.url, $loggedIn) || '#'}
					style="text-decoration: none; cursor: pointer; width: {notSameSize ? 'auto' : '100%'}"
					bind:this={tabElements[index]}
				>
					<TabButton
						{tab}
						borderRadius={resolvedBorderRadius}
						{handleTabClick}
						activeTab={resolvedActiveTab}
						{notSameSize}
						{size}
						{useVariation}
						{autoFitText}
						{vertical}
						{align}
					/>
				</a>
				<!-- eslint-enable svelte/no-navigation-without-resolve -->
			{:else}
				<TabButton
					{tab}
					borderRadius={resolvedBorderRadius}
					{handleTabClick}
					activeTab={resolvedActiveTab}
					{notSameSize}
					{size}
					{useVariation}
					{autoFitText}
					bind:el={tabElements[index]}
					{vertical}
					{align}
				/>
			{/if}
		{/each}
	</div>
{/if}

<style>
	div.tabs {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		border-radius: 16px;
		background-color: var(--color-background-100);
		padding: 0.25rem;
}

	.tabs {
		z-index: 10;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 800;
		text-transform: uppercase;
		--tw-text-opacity: 1;
		color: rgb(229 231 235 / var(--tw-text-opacity, 1));
		margin-top: 0 !important;
}

	div.tabs-alternative {
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-300);
}

	div.tabs-flat-neutral-surface {
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-900);
}

	div.tabs-flat-neutral-surface :global(button.inactive-tab),
	div.tabs-flat-neutral-surface :global(button.inactive-tab .button-text) {
		color: var(--color-text-500) !important;
	}

	div.tabs-alternative-2 {
		position: relative;
		background-color: transparent;
		padding: 0.375rem;
}

	div.tabs-alternative-4 {
		border-width: 0px;
}

	div.variation {
		gap: 0.5rem;
		background-color: transparent;
		display: grid;
		grid-template-columns: repeat(var(--alt-cols, 2), minmax(0, 1fr));
		align-items: stretch;
}

	.variation > * {
		width: 100%;
}

	.vertical-tabs {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		align-items: stretch;
}
	.vertical-tabs > * {
		width: 100%;
}

	.vertical-tabs-alternative2 {
		position: relative;
		padding: 0.375rem;
		background: radial-gradient(
			100% 100% at 0% 50%,
			rgba(255, 255, 255, 0) 0%,
			rgba(255, 255, 255, 0.2) 100%
		);
		box-shadow: inset -2px 6px 9.7px -4px rgba(255, 255, 255, 0.1);
}

	.vertical-tabs-alternative2::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		padding: 1px;
		box-sizing: border-box;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
	}

	div.tabs-momentum {
		background: rgb(from var(--color-text-100) r g b / 0.05) !important;
		gap: 0 !important;
		padding: 4px !important;
	}

	.tabs-transparent {
		background: transparent !important;
	}

	div.tabs-transparent :global(.tab-button),
	div.tabs-transparent :global(button) {
		height: 48px !important;
		text-transform: none !important;
	}

	div.tabs-transparent :global(.button-text) {
		text-transform: none !important;
	}

	div.tabs-transparent :global(button:not(.inactive-tab)),
	div.tabs-transparent :global(button:not(.inactive-tab):hover),
	div.tabs-transparent :global(button:not(.inactive-tab):focus),
	div.tabs-transparent :global(button:not(.inactive-tab):active) {
		background: rgb(from var(--color-text-100) r g b / 0.05) !important;
		color: var(--color-text-100) !important;
		opacity: 1 !important;
	}

	div.tabs-transparent
		:global(
			button:is(.inactive-tab, .solid-static-inactive-tab, .tertiary-solid-static.inactive-tab)
		),
	div.tabs-momentum
		:global(
			button:is(.inactive-tab, .solid-static-inactive-tab, .tertiary-solid-static.inactive-tab):is(
					:hover,
					:focus,
					:active,
					:focus-visible
				)
		) {
		background: transparent !important;
		opacity: 0.5 !important;
	}

	div.tabs.tabs-light {
		background-color: var(--color-text-300) !important;
	}

	div.tabs.tabs-light
		:global(
			button:is(
				.inactive-tab,
				.inactive-tab-contrast,
				.solid-static-inactive-tab,
				.contrast-inactive-tab,
				.glass-inactive-tab,
				.glass-vertical-inactive-tab
			)
		),
	div.tabs.tabs-light
		:global(
			button:is(
					.inactive-tab,
					.inactive-tab-contrast,
					.solid-static-inactive-tab,
					.contrast-inactive-tab,
					.glass-inactive-tab,
					.glass-vertical-inactive-tab
				)
				.button-text
		) {
		color: var(--color-text-900) !important;
	}
</style>
